import * as msal from '@azure/msal-browser'
import {computed, makeAutoObservable, runInAction} from 'mobx'
import {AccountInfo} from '@azure/msal-common'
import {appStore, config, loaderStore} from '../index'
import {LocalStorageClear, LocalStorageGet, LocalStorageRemove, LocalStorageSet} from './Helpers'
import {PromiseCompletion} from './PromiseCompletion'
import {ApiService} from "./ApiService";
import {ApiURL} from "../AppConstants";
import {CustomerDTO, SubscriptionDTO} from "../Models/ApiModels";

type JwtToken = {
    aio: string;
    amr: string[];
    aud: string;
    exp: number;
    family_name: string;
    given_name: string;
    iat: number;
    ipaddr: string;
    iss: string;
    name: string;
    nbf: number;
    nonce: string;
    oid: string;
    rh: string;
    roles: string[];
    sub: string;
    tid: string;
    unique_name: string;
    upn: string;
    uti: string;
    ver: string;
};

type AccountClaims = {
    extension_Company: string
    extension_Regions: string
    extension_Plants: string
    extension_Departments: string
    extension_RecipeManagement: boolean
    extension_Role: string[]
    exp: number
}
type UserInfo = {
    accessToken: string
    account: AccountInfo
}
const TOKEN_KEY = 'currentToken'
const USER_KEY = 'currentUser'
const TERM_KEY = 'terms'

export class AuthService {
    msalInstance: msal.PublicClientApplication
    currentToken: string = ''
    currentUser: AccountInfo | null = null
    userInfo: UserInfo | null = null
    subscriptions: SubscriptionDTO[] = []
    isLoading: PromiseCompletion = new PromiseCompletion()
    private _tokenRefreshInterval: NodeJS.Timeout | null = null
    private _allowTerm: boolean = false

    constructor() {
        makeAutoObservable(this)
        this.msalInstance = new msal.PublicClientApplication(config.msal.auth)
    }

    private _parseJwtToken (token: string): JwtToken | null {
        if (!token) return null;

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map((c: string) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );

        return JSON.parse(jsonPayload);
    }
    private _validateToken (token: string) {
        const payload = this._parseJwtToken(token);
        if (!payload)
            return false;
        const nowUtc = Date.now();
        const expireAt = new Date(payload.exp * 1000);
        return expireAt.getTime() > nowUtc;
    };

    @computed
    public get isTokenValid () {
        return !!this.currentToken && this._validateToken(this.currentToken);
    }

    public get isTermsAcceptedForCurrent() {
        if (!this._allowTerm) return true
        if (this.userInfo) {
            const terms = LocalStorageGet<string[]>(TERM_KEY) || []
            return terms.includes(this.userInfo.account.username)
        }
        return true
    }

    public get isAdmin() {
        return (this.currentUser?.idTokenClaims as AccountClaims)?.extension_Role.includes('Admin')
    }

    public get isSSMMember() {
        return (this.currentUser?.idTokenClaims as AccountClaims)?.extension_Company === '*'
    }

    public get isCompanyAdmin() {
        return this.isAdmin &&
            (((this.currentUser?.idTokenClaims as AccountClaims)?.extension_Company as string).length > 1) &&
            ((this.currentUser?.idTokenClaims as AccountClaims)?.extension_Regions === '*') &&
            ((this.currentUser?.idTokenClaims as AccountClaims)?.extension_Plants === '*') &&
            ((this.currentUser?.idTokenClaims as AccountClaims)?.extension_Departments === '*')
    }

    public get isRecipeManagement() {
        return (this.currentUser?.idTokenClaims as AccountClaims)?.extension_RecipeManagement === true
    }

    public get hasDashboardSubscription() {
        return this.isSSMMember || this.subscriptions.findIndex(s => s.name === 'Dashboard') !== -1
    }

    public get hasRecipeManagementAccess() {
        return this.isSSMMember || (this.subscriptions.findIndex(s => s.name === 'Recipe Management') !== -1 && this.isRecipeManagement)
    }

    public async init() {
         await this._selectAccount()
    }

    public acceptTerms() {
        if (this.userInfo) {
            void this._setCurrentUser(this.userInfo)
            const terms = LocalStorageGet<string[]>(TERM_KEY) || []
            terms.push(this.userInfo.account.username)
            LocalStorageSet<string[]>(TERM_KEY, terms)
        }
    }

    public signIn() {
        const loginRequest = {
            scopes: ['openid', ...config.msal.api.b2cScopes],
        }
        loaderStore.showAppLoader()
        this.msalInstance.loginPopup(loginRequest).then(async response => {
            await this._handleResponse(response)
            appStore.init()
        }).catch(() => {
            LocalStorageClear()
            this._clearUserData()
        }).finally(() => {
            loaderStore.hideAppLoader()
        })
    }

    public initTokenRefresh() {
        this.stopTokenAutoRefresh()
        this._tokenRefreshInterval = setTimeout(async () => {
            await this.refreshToken()
            this.initTokenRefresh();
        }, 30 * 60 * 1000)
    }

    public stopTokenAutoRefresh() {
        if (this._tokenRefreshInterval !== null) {
            clearInterval(this._tokenRefreshInterval)
        }
    }

    public async signOut(signOutUser?: AccountInfo) {
        const user = signOutUser || this.currentUser
        if (user) {
            const logoutRequest = {
                account: this.msalInstance.getAccountByHomeId(user.homeAccountId),
                postLogoutRedirectUri: window.location.origin,
                onRedirectNavigate: this._clearUserData,
            }
            loaderStore.showAppLoader()
            await this.msalInstance.logoutRedirect(logoutRequest)
        }
    }

    public async refreshToken() {
        if (this.currentUser) {
            const conf = {
                scopes: ['openid', ...config.msal.api.b2cScopes],
                account: this.currentUser,
                forceRefresh: true,
            }
            try {
                this.isLoading = new PromiseCompletion()
                const tokenRequestSilent = this.msalInstance.acquireTokenSilent(conf)
                this.isLoading.subscribe(tokenRequestSilent)
                const tokenSilent = await tokenRequestSilent
                await this._handleResponse(tokenSilent)
                return tokenSilent
            } catch (silentError) {
                this._clearUserData();
            }
        }
    }

    private async _setCurrentUser(userInfo: UserInfo) {
        runInAction(() => {
            this._setUser(userInfo.account)
            this._setToken(userInfo.accessToken)
        })
        try {
            if (!this.isTokenValid) {
                await this.refreshToken()
            }
            const companyId = (userInfo.account.idTokenClaims as AccountClaims)?.extension_Company;
            if (companyId !== '*') {
                await this._getCustomerSubscription(companyId)
            }
            this.initTokenRefresh()
        } catch (e) {

        }
    }

    private _handleResponse = async (response: any) => {
        if (response !== null) {
            const {account, accessToken} = response
            this.userInfo = {account, accessToken}
            if (this.isTermsAcceptedForCurrent) {
                await this._setCurrentUser(this.userInfo)
            }
        }
    }

    private _setUser(user: AccountInfo | null) {
        this.currentUser = user
        user ? LocalStorageSet(USER_KEY, user) : LocalStorageRemove(USER_KEY)
    }

    private _setToken(token: string) {
        this.currentToken = token
        token ? LocalStorageSet(TOKEN_KEY, token) : LocalStorageRemove(TOKEN_KEY)
    }

    private _clearUserData = () => {
        this.userInfo = null
        this._setUser(null)
        this._setToken('')
    }

    private async _getCustomerSubscription(companyId: string) {
        this.isLoading = new PromiseCompletion()
        const subscriptionsRequest = ApiService.getData<CustomerDTO>(`${ApiURL.Customer}/${companyId}`)
        this.isLoading.subscribe(subscriptionsRequest)
        const {data} = await subscriptionsRequest
        this.subscriptions = data.subscriptions
    }

    private async _selectAccount() {
        const currentAccounts = this.msalInstance.getAllAccounts()
        const token = LocalStorageGet<string>(TOKEN_KEY)
        if (!!currentAccounts.length && token && this._validateToken(token || '')) {
            this.userInfo = {account: currentAccounts[0], accessToken: token || ''}
            if (!this.isTermsAcceptedForCurrent) {
                loaderStore.showAppLoader()
                this._clearUserData()
                await this.signOut(currentAccounts[0])
            } else {
                await this._setCurrentUser(this.userInfo)
                appStore.init()
            }
        } else {
            this._clearUserData();
        }
    }
}
